/**
 * usePropertySummary Hook
 *
 * @module features/releases/hooks/usePropertySummary
 * @description Fetches health summary for a property including untested commits,
 * last run status, total issues, and regression pass rate.
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import type { ReleaseQARunStatus } from '../releaseTypes';
import type { BuildHead } from './useReleaseProperties';

type CommitPropertySummary = {
  mode: 'commit';
  propertyId: string;
  untestedCommits: number;
  lastRunId: string | null;
  lastRunStatus: ReleaseQARunStatus | null;
  lastRunAt: string | null;
  totalIssuesRecent: number;
  recentRunCount: number;
  regressionPassRate: number | null;
};

/** A pushed-image application's summary: its newest ready main build. */
type BuildPropertySummary = {
  mode: 'build';
  propertyId: string;
  head: BuildHead | null;
};

export type PropertyHealthSummary = CommitPropertySummary | BuildPropertySummary;

type PropertySummaryResponse = {
  ok: boolean;
  summary: PropertyHealthSummary;
};

/**
 * Fetch health summary for a property.
 */
export function usePropertySummary(propertyId: string) {
  return useQuery({
    queryKey: ['release-qa-property-summary', propertyId],
    queryFn: async (): Promise<PropertyHealthSummary | null> => {
      const response = await fetch(`${API_URL}/release-qa/properties/${propertyId}/summary`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw await toApiError(response, 'Failed to fetch property summary');
      }

      const data: PropertySummaryResponse = await response.json();
      return data.summary;
    },
    enabled: !!propertyId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}
